import { Constants } from "@eagerdog/constants";

/**
 * AKC Fast CAT math — one implementation shared by the API (run import /
 * recalculation), the web and the mobile manual-entry forms.
 *
 * Rules:
 * - Course is 100 yards
 * - MPH = (100 yards / time_seconds) * (3600 sec/hour / 1760 yards/mile)
 * - Points = MPH * handicap multiplier
 * - Handicap by height at withers: >=18" 1.0x, 12–<18" 1.5x, <12" 2.0x
 */

export const FAST_CAT_COURSE_LENGTH_YARDS = 100;
export const FAST_CAT_YARDS_PER_MILE = 1760;
export const FAST_CAT_SECONDS_PER_HOUR = 3600;

const round2 = (n: number): number => Math.round(n * 100) / 100;

export const fastCatCalculateMPH = (timeSeconds: number): number => {
  if (!(timeSeconds > 0)) {
    throw new Error("Time must be greater than 0");
  }
  const mph =
    (FAST_CAT_COURSE_LENGTH_YARDS / timeSeconds) * (FAST_CAT_SECONDS_PER_HOUR / FAST_CAT_YARDS_PER_MILE);
  return round2(mph);
};

export const fastCatHandicapMultiplier = (heightAtWithersInches: number): number => {
  if (heightAtWithersInches >= 18) return 1.0;
  if (heightAtWithersInches >= 12) return 1.5;
  return 2.0;
};

export const fastCatHeightClass = (heightAtWithersInches: number): string => {
  if (heightAtWithersInches >= 18) return Constants.fast_cat_height_class.eighteen_plus;
  if (heightAtWithersInches >= 12) return Constants.fast_cat_height_class.twelve_to_eighteen;
  return Constants.fast_cat_height_class.under_twelve;
};

export const fastCatCalculatePoints = (mph: number, handicapMultiplier: number): number =>
  round2(mph * handicapMultiplier);

export interface IFastCatRunMetrics {
  mph: number;
  handicap_multiplier: number;
  height_class: string;
  points_earned: number;
}

export const fastCatRunMetrics = (timeSeconds: number, heightAtWithersInches: number): IFastCatRunMetrics => {
  const mph = fastCatCalculateMPH(timeSeconds);
  const handicap_multiplier = fastCatHandicapMultiplier(heightAtWithersInches);
  const height_class = fastCatHeightClass(heightAtWithersInches);
  return { mph, handicap_multiplier, height_class, points_earned: fastCatCalculatePoints(mph, handicap_multiplier) };
};

/**
 * Safe variant for live form previews: returns null instead of throwing when
 * inputs are missing or invalid.
 */
export const fastCatPreviewMetrics = (
  timeSeconds: number | string | undefined | null,
  heightAtWithersInches: number | string | undefined | null
): IFastCatRunMetrics | null => {
  const time = typeof timeSeconds === "number" ? timeSeconds : parseFloat(String(timeSeconds ?? ""));
  const height =
    typeof heightAtWithersInches === "number" ? heightAtWithersInches : parseFloat(String(heightAtWithersInches ?? ""));
  if (!(time > 0) || !(height > 0)) return null;
  return fastCatRunMetrics(time, height);
};
